import { useCallback, useMemo } from "react";
import {
  getPromptConfigInstructions,
  createPromptConfigInstructions,
  updatePromptConfigInstructions,
  getPromptConfigTools,
  createPromptConfigTool,
  updatePromptConfigTool,
  deletePromptConfigTool,
  getPromptConfigSkills,
  createPromptConfigSkill,
  updatePromptConfigSkill,
  deletePromptConfigSkill,
  getPromptConfigMcpServers,
  createPromptConfigMcpServer,
  updatePromptConfigMcpServer,
  deletePromptConfigMcpServer,
} from "../../services/prompt-ai";
import { useAgentScope } from "./sub-agent-hook";

/**
 * Data source for the agent config pages.
 *
 * The same pages serve the top-level agent and each sub-agent. Only where the
 * data lives differs, so this hook hands back one CRUD shape with two backings:
 *
 *   agent scope     -> app-level prompt-config API (one record per app)
 *   sub-agent scope -> the sub-agent's own `toolData`, patched through the
 *                      tools API, under one key per section:
 *                      instructions / tools / skills / mcpServers / llm
 *
 * Both branches return the same shapes the pages already expect, so the pages
 * keep their existing render logic untouched:
 *   instructions.get()             -> object | null
 *   instructions.save(payload)     -> { status }
 *   <section>.list()               -> array
 *   <section>.save(payload, id?)   -> { status }
 *   <section>.remove(id)           -> void
 *
 * Sub-agent collections are stored as maps keyed by name rather than as rows
 * with server ids, so this layer projects each map into id-bearing records
 * (`id` = the map key) and folds edits back into the map.
 *
 * NOTE: the runtime currently executes a sub-agent from `instructions` and
 * `tools` only (see agent_prompt_based.py). Skills, MCP and LLM are persisted
 * here so the configuration is ready, but they take effect only once the
 * runtime reads them.
 */
export function useAgentConfigSource() {
  const {
    appId,
    subAgentId,
    subAgent,
    toolData,
    saveToolData,
    saveMeta,
    loading,
    error,
  } = useAgentScope();

  const isSubAgent = !!subAgentId;

  // ── instructions ──────────────────────────────────────────────────────────
  const instructionsGet = useCallback(async () => {
    if (!isSubAgent) return await getPromptConfigInstructions(appId);
    // A sub-agent has a single free-text instruction rather than the app-level
    // record, so surface it under the same key the page reads.
    return { globalPrompt: toolData.instructions || "" };
  }, [isSubAgent, appId, toolData]);

  const instructionsSave = useCallback(
    async (payload, isExisting) => {
      if (!isSubAgent) {
        return isExisting
          ? await updatePromptConfigInstructions(appId, payload)
          : await createPromptConfigInstructions(appId, payload);
      }
      const { ok } = await saveToolData({
        instructions: payload.globalPrompt || "",
      });
      return { status: ok ? 200 : 500 };
    },
    [isSubAgent, appId, saveToolData]
  );

  // ── tools ─────────────────────────────────────────────────────────────────
  const toolsList = useCallback(async () => {
    if (!isSubAgent) {
      const data = await getPromptConfigTools(appId);
      const all = Array.isArray(data) ? data : [];
      // sub-agents are stored alongside tools but are managed on their own page
      return all.filter((t) => t.type !== "sub_agent");
    }
    const map = toolData.tools || {};
    return Object.keys(map).map((key) => ({ ...map[key], id: key }));
  }, [isSubAgent, appId, toolData]);

  const toolsSave = useCallback(
    async (payload, toolId) => {
      if (!isSubAgent) {
        return toolId
          ? await updatePromptConfigTool(appId, toolId, payload)
          : await createPromptConfigTool(appId, payload);
      }
      const map = { ...(toolData.tools || {}) };
      const name = (payload.name || "").trim();
      if (!name) return { status: 400 };
      // renaming re-keys the entry, so drop the old key first
      if (toolId && toolId !== name) delete map[toolId];
      map[name] = {
        type: payload.type,
        name,
        description: payload.description || "",
        parameters: payload.parameters || {},
        toolData: payload.toolData || {},
      };
      const { ok } = await saveToolData({ tools: map });
      return { status: ok ? 200 : 500 };
    },
    [isSubAgent, appId, toolData, saveToolData]
  );

  const toolsRemove = useCallback(
    async (toolId) => {
      if (!isSubAgent) return await deletePromptConfigTool(appId, toolId);
      const map = { ...(toolData.tools || {}) };
      delete map[toolId];
      await saveToolData({ tools: map });
    },
    [isSubAgent, appId, toolData, saveToolData]
  );

  // ── skills ────────────────────────────────────────────────────────────────
  const skillsList = useCallback(async () => {
    if (!isSubAgent) {
      const data = await getPromptConfigSkills(appId);
      return Array.isArray(data) ? data : [];
    }
    const map = toolData.skills || {};
    return Object.keys(map).map((key) => ({ ...map[key], id: key }));
  }, [isSubAgent, appId, toolData]);

  const skillsSave = useCallback(
    async (payload, skillId) => {
      if (!isSubAgent) {
        return skillId
          ? await updatePromptConfigSkill(appId, skillId, payload)
          : await createPromptConfigSkill(appId, payload);
      }
      const map = { ...(toolData.skills || {}) };
      const name = (payload.name || "").trim();
      if (!name) return { status: 400 };
      if (skillId && skillId !== name) delete map[skillId];
      map[name] = { ...payload, name };
      const { ok } = await saveToolData({ skills: map });
      return { status: ok ? 200 : 500 };
    },
    [isSubAgent, appId, toolData, saveToolData]
  );

  const skillsRemove = useCallback(
    async (skillId) => {
      if (!isSubAgent) return await deletePromptConfigSkill(appId, skillId);
      const map = { ...(toolData.skills || {}) };
      delete map[skillId];
      await saveToolData({ skills: map });
    },
    [isSubAgent, appId, toolData, saveToolData]
  );

  // ── mcp servers ───────────────────────────────────────────────────────────
  const mcpList = useCallback(async () => {
    if (!isSubAgent) {
      const data = await getPromptConfigMcpServers(appId);
      return Array.isArray(data) ? data : [];
    }
    const map = toolData.mcpServers || {};
    return Object.keys(map).map((key) => ({ ...map[key], id: key }));
  }, [isSubAgent, appId, toolData]);

  const mcpSave = useCallback(
    async (payload, serverId) => {
      if (!isSubAgent) {
        return serverId
          ? await updatePromptConfigMcpServer(appId, serverId, payload)
          : await createPromptConfigMcpServer(appId, payload);
      }
      const map = { ...(toolData.mcpServers || {}) };
      const name = (payload.name || "").trim();
      if (!name) return { status: 400 };
      if (serverId && serverId !== name) delete map[serverId];
      map[name] = { ...payload, name };
      const { ok } = await saveToolData({ mcpServers: map });
      return { status: ok ? 200 : 500 };
    },
    [isSubAgent, appId, toolData, saveToolData]
  );

  const mcpRemove = useCallback(
    async (serverId) => {
      if (!isSubAgent) return await deletePromptConfigMcpServer(appId, serverId);
      const map = { ...(toolData.mcpServers || {}) };
      delete map[serverId];
      await saveToolData({ mcpServers: map });
    },
    [isSubAgent, appId, toolData, saveToolData]
  );

  // ── llm ───────────────────────────────────────────────────────────────────
  // App-level LLM settings live in their own service; a sub-agent only stores an
  // optional override, and inherits the agent's settings when unset.
  const llmGet = useCallback(async () => {
    if (!isSubAgent) return null;
    return toolData.llm || {};
  }, [isSubAgent, toolData]);

  const llmSave = useCallback(
    async (llm) => {
      if (!isSubAgent) return { status: 400 };
      const { ok } = await saveToolData({ llm });
      return { status: ok ? 200 : 500 };
    },
    [isSubAgent, saveToolData]
  );

  return useMemo(
    () => ({
      appId,
      subAgentId,
      subAgent,
      isSubAgent,
      // sub-agent scope must wait for its record before reading toolData
      scopeLoading: isSubAgent ? loading : false,
      scopeError: isSubAgent ? error : false,
      // top-level sub-agent fields (name / description), not part of toolData
      saveMeta,
      instructions: { get: instructionsGet, save: instructionsSave },
      tools: { list: toolsList, save: toolsSave, remove: toolsRemove },
      skills: { list: skillsList, save: skillsSave, remove: skillsRemove },
      mcp: { list: mcpList, save: mcpSave, remove: mcpRemove },
      llm: { get: llmGet, save: llmSave },
    }),
    [
      appId,
      subAgentId,
      subAgent,
      isSubAgent,
      loading,
      error,
      saveMeta,
      instructionsGet,
      instructionsSave,
      toolsList,
      toolsSave,
      toolsRemove,
      skillsList,
      skillsSave,
      skillsRemove,
      mcpList,
      mcpSave,
      mcpRemove,
      llmGet,
      llmSave,
    ]
  );
}
